import { z } from 'zod'

export const PlaceFeatureGroupSchema = z.enum([
  'account',
  'residents',
  'community',
  'chat',
  'operations',
  'packages',
  'amenities',
  'commerce',
  'staff',
  'keyless',
  'smartHome',
  'other',
])

export const PlaceFeatureGroup = PlaceFeatureGroupSchema.enum

export type PlaceFeatureGroup = z.infer<typeof PlaceFeatureGroupSchema>

export const PlaceFeatureTabSchema = z.enum(['general', 'features', 'accessControl', 'other'])

export const PlaceFeatureTab = PlaceFeatureTabSchema.enum

export type PlaceFeatureTab = z.infer<typeof PlaceFeatureTabSchema>

export const PLACE_FEATURE_GROUP_TAB: Record<PlaceFeatureGroup, PlaceFeatureTab> = {
  account: PlaceFeatureTab.general,
  amenities: PlaceFeatureTab.features,
  chat: PlaceFeatureTab.features,
  commerce: PlaceFeatureTab.features,
  community: PlaceFeatureTab.features,
  keyless: PlaceFeatureTab.accessControl,
  operations: PlaceFeatureTab.features,
  other: PlaceFeatureTab.other,
  packages: PlaceFeatureTab.features,
  residents: PlaceFeatureTab.features,
  smartHome: PlaceFeatureTab.accessControl,
  staff: PlaceFeatureTab.features,
}

export interface PlaceFeatureDefinition {
  key: string
  group: PlaceFeatureGroup
  // Note: a child is disabled while its parent is off and is cleared when the parent is turned off
  parent?: string
  // Note: cross-group switches that must all be on (e.g. Amenities ACR needs Salto Space)
  requires?: readonly string[]
  adminOnly?: boolean
  // Note: not a UI switch, only derived and sent to the backend
  hidden?: boolean
  // TODO: drop the ghost flags once the deployed backend no longer stores them (BACKEND_IMPROVEMENTS.md, section 6)
  // Note: the flag is gone from the newer backend model, so a save may not persist it
  ghost?: boolean
}

export const PLACE_FEATURES = [
  { group: 'account', key: 'active' },
  { group: 'account', key: 'accountPastDue' },
  { group: 'account', key: 'accountSuspended' },
  { group: 'account', key: 'isHoa' },
  { group: 'account', key: 'avatarChangeRestricted', parent: 'isHoa' },
  { group: 'account', key: 'nameChangeRestricted' },

  { group: 'residents', key: 'servicesAvailable' },
  { group: 'residents', key: 'buildingEssentialsAvailable' },
  { group: 'residents', key: 'perksAvailable' },
  { group: 'residents', key: 'homepageAvailable' },
  { group: 'residents', key: 'appUserStatusAvailable' },
  { group: 'residents', key: 'amenityPassesAvailable' },
  { group: 'residents', key: 'manualApproval' },
  { group: 'residents', key: 'verifyPhoneNumber' },
  { group: 'residents', key: 'fitnessAvailable' },
  { group: 'residents', key: 'mealMeAvailable' },
  { group: 'residents', key: 'customerPortalAvailable' },
  { group: 'residents', key: 'residentEventsAvailable' },

  { group: 'community', key: 'communityCalendar' },
  { group: 'community', key: 'groupsAvailable' },
  { group: 'community', key: 'groupsPostReview' },
  { group: 'community', key: 'communicationAvailable' },
  { group: 'community', key: 'communicateSMSAvailable' },
  { group: 'community', key: 'qrCodeSurveysAvailable' },
  { group: 'community', key: 'tvAppAvailable' },
  { ghost: true, group: 'community', key: 'websiteWidgetsAvailable' },
  { group: 'community', key: 'eventTicketsAvailable' },

  { group: 'chat', key: 'chatAvailable' },
  { group: 'chat', key: 'widgetChatAvailable', parent: 'chatAvailable' },
  { group: 'chat', key: 'chatPM', parent: 'chatAvailable' },
  { group: 'chat', key: 'chatFD', parent: 'chatAvailable' },
  { group: 'chat', key: 'chatMT', parent: 'chatAvailable' },
  { group: 'chat', key: 'chatCustom' },
  { group: 'chat', key: 'aiWriterAvailable' },
  { group: 'chat', key: 'maintenanceCallAvailable' },
  { group: 'chat', key: 'maintenanceEscalationAvailable' },

  { group: 'operations', key: 'maintenanceRequestAvailable' },
  { adminOnly: true, ghost: true, group: 'operations', key: 'requiredScheduledDate' },
  { group: 'operations', key: 'checklistsAvailable' },
  { group: 'operations', key: 'inspectionsAvailable' },
  { group: 'operations', key: 'incidentReportsAvailable' },
  { group: 'operations', key: 'assetTrackingAvailable' },

  { group: 'packages', key: 'packagesAvailable' },
  { group: 'packages', key: 'packagesQuickScanAvailable', parent: 'packagesAvailable' },
  { group: 'packages', key: 'packagesD2DAvailable' },
  { group: 'packages', key: 'packageRoomAvailable' },
  { group: 'packages', key: 'guestAccess' },
  { group: 'packages', key: 'physicalKeyAvailable', parent: 'guestAccess' },
  { group: 'packages', key: 'customAccessAvailable', parent: 'guestAccess' },
  { group: 'packages', key: 'visitorParkingAvailable' },
  { group: 'packages', key: 'parkingManagementAvailable' },
  { group: 'packages', key: 'valetAvailable' },

  { group: 'amenities', key: 'amenityReservations' },
  { group: 'amenities', key: 'amenityLocationAvailable' },
  { group: 'amenities', key: 'amenityMarketplaceAvailable' },
  { group: 'amenities', key: 'amenityRewardsAvailable' },
  {
    group: 'amenities',
    key: 'amenitiesAcrAvailable',
    parent: 'amenityReservations',
    requires: ['saltoSpaceAvailable'],
  },

  { group: 'commerce', key: 'marketShopAvailable' },
  { group: 'commerce', key: 'marketShopRestaurantAvailable', parent: 'marketShopAvailable' },
  { group: 'commerce', key: 'rewardsAvailable' },
  { group: 'commerce', key: 'loyaltyAvailable' },
  { ghost: true, group: 'commerce', key: 'rentAvailable' },

  { group: 'staff', key: 'frontDeskAvailable' },
  { group: 'staff', key: 'managementUsersAvailable' },
  { group: 'staff', key: 'restaurantManagerAvailable' },
  { group: 'staff', key: 'buildingAnalyticsAvailable' },
  { group: 'staff', key: 'reputationAvailable' },
  { group: 'staff', key: 'displayBuildingNumber' },

  { group: 'keyless', key: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'keylessAccessForStaffAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'butterflyAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'butterflyVisitorAccessAvailable', parent: 'butterflyAvailable' },
  { group: 'keyless', key: 'bmxInAppCallsAvailable', parent: 'butterflyAvailable' },
  { group: 'keyless', key: 'latchAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'brivoAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'brivoVisitorAccessAvailable', parent: 'brivoAvailable' },
  { group: 'keyless', key: 'openpathAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'twoNAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'dormakabaAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'saltoKSAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'saltoSpaceAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'saltoSpaceUserSyncAvailable', parent: 'saltoSpaceAvailable' },
  { group: 'keyless', key: 'saltoSpaceVisitorAccessAvailable', parent: 'saltoSpaceAvailable' },
  { group: 'keyless', key: 'saltoSpaceFobsAvailable', parent: 'saltoSpaceAvailable' },
  { group: 'keyless', key: 'saltoSpacePinVisitorAccessAvailable', parent: 'saltoSpaceAvailable' },
  { group: 'keyless', key: 'saltoSpaceDeviceBindingAvailable', parent: 'saltoSpaceAvailable' },
  { group: 'keyless', key: 'swiftlaneAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'swiftlaneVisitorAccessAvailable', parent: 'swiftlaneAvailable' },
  { group: 'keyless', key: 'swiftlaneVoipAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'swiftlaneAutoUserAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'akuvoxAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'rentlyAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'remoteLockAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'remoteLockFobAvailable', parent: 'remoteLockAvailable' },
  { group: 'keyless', key: 'remoteLockVisitorAccessAvailable', parent: 'remoteLockAvailable' },
  { group: 'keyless', key: 'pointCentralAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'pointCentralLocksAvailable', parent: 'pointCentralAvailable' },
  { group: 'keyless', key: 'pointCentralThermostatsAvailable', parent: 'pointCentralAvailable' },
  { group: 'keyless', key: 'iglooWorkAvailable', parent: 'keylessAccessAvailable' },
  { group: 'keyless', key: 'iglooWorkVisitorAccessAvailable', parent: 'iglooWorkAvailable' },
  { group: 'keyless', key: 'iglooWorkStaffMasterKeyAvailable', parent: 'iglooWorkAvailable' },
  { group: 'keyless', key: 'iglooWorkCustomPinAvailable', parent: 'iglooWorkAvailable' },
  { group: 'keyless', key: 'iglooWorkAlgoPinAvailable', parent: 'iglooWorkAvailable' },
  {
    group: 'keyless',
    hidden: true,
    key: 'iglooWorkStaffAccessAvailable',
    parent: 'iglooWorkAvailable',
  },
  { group: 'keyless', key: 'visionlineAvailable', parent: 'keylessAccessAvailable' },

  { group: 'smartHome', key: 'coolAutomationAvailable' },
  { group: 'smartHome', key: 'ecobeeAvailable' },
  { group: 'smartHome', key: 'honeywellAvailable' },

  { group: 'other', key: 'parcelPendingAvailable' },
] as const satisfies readonly PlaceFeatureDefinition[]

export type PlaceFeatureKey = (typeof PLACE_FEATURES)[number]['key']

export type PlaceFlags = Partial<Record<PlaceFeatureKey, boolean>>

export const KEYLESS_MASTER_KEY = 'keylessAccessAvailable' satisfies PlaceFeatureKey

export const IGLOO_KEY = 'iglooWorkAvailable' satisfies PlaceFeatureKey

// Note: sent on every save - the backend runs its disabling validation when these are absent
// while stored on, and a missing code looks like a duplicate of every other building
export const ALWAYS_SENT_FLAGS = [
  'amenityLocationAvailable',
  'eventTicketsAvailable',
] as const satisfies readonly PlaceFeatureKey[]
